import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: "▹";
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: "";
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'Distributed Systems',
    'Operating Systems',
    'Disk Management',
    'Computer Networks',
    'Python',
    'C++',
    'Go',
    'Linux/Unix',
    'Protobuf',
    'Docker',
    'AWS',
    'Splunk',
    'Kafka',
    'NoSQL',
    'SQL',
    'Version Control',
  ];
  // C, C++, Python, C#, JavaScript, Problem Solving, Django, Django Rest Framework, .NET,
  // Entity Framework, LINQ, Flask, Node, ReactJs, AngularJs, AJAX, Database Design, SQL, MSSQL
  // PostgreSQL, MySQL, MongoDb, Neo4j, AWS, EC2, S3, Route 53, RDS, Cloud, Git, BitBucket
  // Confluence, JIRA, Linux, Shell, Ubuntu, Docker, Tensorflow, PyTorch, Data Analytics

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Currently, I'm working at{' '}
              <a href="https://www.nutanix.com/">Nutanix</a> in the Core Data
              Path team, expanding my expertise in Disk Management and
              Distributed Systems. My previous experience spans roles at{' '}
              <a href="https://www.tesla.com/">Tesla Motors</a> and{' '}
              <a href="https://www.barclays.in/">Barclays</a>, where I navigated
              the complexities of large-scale Manufacturing Engineering Systems
              and Fintech. <br />
              <br />
              Driven by a competitive nature, I frequently participate in
              hackathons. Beyond work, I'm committed to personal growth and
              knowledge sharing, notably with{' '}
              <a href="https://www3.cs.stonybrook.edu/~mueller/">
                Prof. Klaus Mueller
              </a>{' '}
              in the VAI Lab at Stony Brook University and{' '}
              <a href="https://www.kjscecodecell.com/">KJSCE CodeCell</a>.
            </p>

            <p>Few technologies I’m experienced in:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
